import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
      <div className="max-w-6xl mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-gray-900 mb-4" data-testid="home-title">
            Google Reviews Widget System
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            Centralized multi-tenant reputation management platform
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link to="/admin" data-testid="admin-link">
              <button className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors">
                Admin Dashboard
              </button>
            </Link>
            <Link to="/demo" data-testid="demo-link">
              <button className="px-6 py-3 bg-white text-blue-600 border-2 border-blue-600 rounded-lg font-medium hover:bg-blue-50 transition-colors">
                View Demo Widget
              </button>
            </Link>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-16">
          <div className="bg-white rounded-xl p-8 shadow-md">
            <h3 className="text-2xl font-semibold mb-4 text-gray-900">Script Embed</h3>
            <p className="text-gray-600 mb-4">Modern, recommended approach</p>
            <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm overflow-x-auto">
              <code>{`<div id="gmb-widget"></div>
<script src="${BACKEND_URL}/api/widget.js" 
  data-client="CLIENT_ID">
</script>`}</code>
            </div>
          </div>

          <div className="bg-white rounded-xl p-8 shadow-md">
            <h3 className="text-2xl font-semibold mb-4 text-gray-900">Iframe Embed</h3>
            <p className="text-gray-600 mb-4">Backward compatible option</p>
            <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm overflow-x-auto">
              <code>{`<iframe 
  src="${BACKEND_URL}/api/widget?client=CLIENT_ID" 
  width="100%" 
  height="440"
  style="border:none;border-radius:8px">
</iframe>`}</code>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-8 shadow-md">
          <h3 className="text-2xl font-semibold mb-6 text-gray-900">Key Features</h3>
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <div className="text-3xl mb-2">🏢</div>
              <h4 className="font-semibold text-lg mb-2">Multi-Tenant</h4>
              <p className="text-gray-600 text-sm">Manage unlimited clients with individual configurations</p>
            </div>
            <div>
              <div className="text-3xl mb-2">💾</div>
              <h4 className="font-semibold text-lg mb-2">Smart Caching</h4>
              <p className="text-gray-600 text-sm">24-hour cache to minimize API costs</p>
            </div>
            <div>
              <div className="text-3xl mb-2">📊</div>
              <h4 className="font-semibold text-lg mb-2">Reputation Management</h4>
              <p className="text-gray-600 text-sm">Capture negative feedback privately</p>
            </div>
            <div>
              <div className="text-3xl mb-2">🎨</div>
              <h4 className="font-semibold text-lg mb-2">Customizable</h4>
              <p className="text-gray-600 text-sm">Per-client theme colors and settings</p>
            </div>
            <div>
              <div className="text-3xl mb-2">⚡</div>
              <h4 className="font-semibold text-lg mb-2">Zero Maintenance</h4>
              <p className="text-gray-600 text-sm">Update once, deploy everywhere</p>
            </div>
            <div>
              <div className="text-3xl mb-2">🔒</div>
              <h4 className="font-semibold text-lg mb-2">Secure</h4>
              <p className="text-gray-600 text-sm">Rate limiting and admin authentication</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Admin = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [clients, setClients] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [showAddClient, setShowAddClient] = useState(false);
  const [selectedTab, setSelectedTab] = useState("clients");
  const [showEmbedModal, setShowEmbedModal] = useState(null);
  const [copiedCode, setCopiedCode] = useState("");
  const [newClient, setNewClient] = useState({
    clientId: "",
    businessUrl: "",
    themeAccentColor: "#4285f4",
    maxReviews: 6,
    googleReviewLink: "",
    isActive: true
  });
  const [editingClient, setEditingClient] = useState(null);

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(type);
    setTimeout(() => setCopiedCode(""), 2000);
  };

  const getIframeCode = (clientId) => {
    return `<iframe src="${BACKEND_URL}/api/widget?client=${clientId}" width="100%" height="440" style="border:none;border-radius:8px"></iframe>`;
  };

  const getScriptCode = (clientId) => {
    return `<div id="gmb-widget"></div>\n<script src="${BACKEND_URL}/api/widget.js" data-client="${clientId}"></script>`;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.get(`${API}/admin/clients`, {
        auth: {
          username: "admin",
          password: password
        }
      });
      setAuthenticated(true);
      setClients(response.data);
    } catch (error) {
      alert("Invalid password");
    }
  };

  const loadClients = async () => {
    try {
      const response = await axios.get(`${API}/admin/clients`, {
        auth: {
          username: "admin",
          password: password
        }
      });
      setClients(response.data);
    } catch (error) {
      console.error("Failed to load clients:", error);
    }
  };

  const loadFeedback = async () => {
    try {
      const response = await axios.get(`${API}/admin/feedback`, {
        auth: {
          username: "admin",
          password: password
        }
      });
      setFeedback(response.data);
    } catch (error) {
      console.error("Failed to load feedback:", error);
    }
  };

  useEffect(() => {
    if (authenticated && selectedTab === "clients") {
      loadClients();
    } else if (authenticated && selectedTab === "feedback") {
      loadFeedback();
    }
  }, [authenticated, selectedTab]);

  const handleAddClient = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/clients`, newClient, {
        auth: {
          username: "admin",
          password: password
        }
      });
      setShowAddClient(false);
      setNewClient({
        clientId: "",
        businessUrl: "",
        themeAccentColor: "#4285f4",
        maxReviews: 6,
        googleReviewLink: "",
        isActive: true
      });
      loadClients();
    } catch (error) {
      alert(error.response?.data?.detail || "Failed to add client");
    }
  };

  const handleUpdateClient = async (clientId) => {
    try {
      await axios.put(`${API}/admin/clients/${clientId}`, editingClient, {
        auth: {
          username: "admin",
          password: password
        }
      });
      setEditingClient(null);
      loadClients();
    } catch (error) {
      alert("Failed to update client");
    }
  };

  const handleToggleActive = async (clientId, isActive) => {
    try {
      await axios.put(`${API}/admin/clients/${clientId}`, { isActive: !isActive }, {
        auth: {
          username: "admin",
          password: password
        }
      });
      loadClients();
    } catch (error) {
      alert("Failed to update client status");
    }
  };

  const handleDeleteClient = async (clientId) => {
    if (!window.confirm(`Are you sure you want to delete "${clientId}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/clients/${clientId}`, {
        auth: {
          username: "admin",
          password: password
        }
      });
      loadClients();
    } catch (error) {
      console.error("Delete error:", error);
      alert("Failed to delete client: " + (error.response?.data?.detail || error.message));
    }
  };

  const [refreshingClient, setRefreshingClient] = useState(null);

  const handleRefreshReviews = async (clientId) => {
    setRefreshingClient(clientId);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/clients/${clientId}/refresh-reviews`, {}, {
        auth: {
          username: "admin",
          password: password
        }
      });
      alert(`Reviews refreshed!\nRating: ${response.data.rating}\nTotal Reviews: ${response.data.totalReviews}\nFetched: ${response.data.reviewCount} reviews`);
    } catch (error) {
      console.error("Refresh error:", error);
      alert("Failed to refresh reviews: " + (error.response?.data?.detail || error.message));
    } finally {
      setRefreshingClient(null);
    }
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-xl shadow-lg p-8 w-full max-w-md">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center" data-testid="admin-login-title">
            Admin Login
          </h2>
          <form onSubmit={handleLogin}>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <input
                type="password"
                data-testid="admin-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
            <button
              type="submit"
              data-testid="admin-login-submit"
              className="w-full bg-blue-600 text-white py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              Login
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-gray-900" data-testid="admin-dashboard-title">Admin Dashboard</h1>
            <Link to="/">
              <button className="text-blue-600 hover:text-blue-700 font-medium">
                ← Back to Home
              </button>
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-6 border-b border-gray-200">
          <div className="flex gap-4">
            <button
              data-testid="clients-tab"
              onClick={() => setSelectedTab("clients")}
              className={`pb-3 px-2 font-medium border-b-2 transition-colors ${
                selectedTab === "clients"
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-gray-600 hover:text-gray-900"
              }`}
            >
              Clients ({clients.length})
            </button>
            <button
              data-testid="feedback-tab"
              onClick={() => setSelectedTab("feedback")}
              className={`pb-3 px-2 font-medium border-b-2 transition-colors ${
                selectedTab === "feedback"
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-gray-600 hover:text-gray-900"
              }`}
            >
              Feedback ({feedback.length})
            </button>
          </div>
        </div>

        {selectedTab === "clients" && (
          <>
            <div className="mb-6 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900">Client Management</h2>
              <button
                data-testid="add-client-btn"
                onClick={() => setShowAddClient(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                + Add New Client
              </button>
            </div>

            {showAddClient && (
              <div className="bg-white rounded-xl shadow-md p-6 mb-6">
                <h3 className="text-lg font-semibold mb-4">Add New Client</h3>
                <form onSubmit={handleAddClient} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Client ID *
                      </label>
                      <input
                        type="text"
                        data-testid="new-client-id"
                        value={newClient.clientId}
                        onChange={(e) => setNewClient({...newClient, clientId: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="spokane-fireplace"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Business URL *
                      </label>
                      <input
                        type="url"
                        data-testid="new-business-url"
                        value={newClient.businessUrl}
                        onChange={(e) => setNewClient({...newClient, businessUrl: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="https://maps.google.com/..."
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Theme Color
                      </label>
                      <input
                        type="color"
                        data-testid="new-theme-color"
                        value={newClient.themeAccentColor}
                        onChange={(e) => setNewClient({...newClient, themeAccentColor: e.target.value})}
                        className="w-full h-10 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Max Reviews
                      </label>
                      <input
                        type="number"
                        data-testid="new-max-reviews"
                        value={newClient.maxReviews}
                        onChange={(e) => setNewClient({...newClient, maxReviews: parseInt(e.target.value)})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        min="1"
                        max="20"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Google Review Link (optional)
                    </label>
                    <input
                      type="url"
                      data-testid="new-review-link"
                      value={newClient.googleReviewLink}
                      onChange={(e) => setNewClient({...newClient, googleReviewLink: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="https://g.page/..."
                    />
                  </div>
                  <div className="flex gap-3">
                    <button
                      type="submit"
                      data-testid="submit-new-client"
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
                    >
                      Add Client
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowAddClient(false)}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Client ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Business URL
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Theme
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Max Reviews
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {clients.map((client) => (
                      <tr key={client.id} data-testid="client-row">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900" data-testid="client-id">
                          {client.clientId}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          <a href={client.businessUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                            View
                          </a>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <div
                              className="w-6 h-6 rounded border border-gray-300"
                              style={{ backgroundColor: client.themeAccentColor }}
                            />
                            <span className="text-sm text-gray-600">{client.themeAccentColor}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {client.maxReviews}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            client.isActive
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`} data-testid="client-status">
                            {client.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <div className="flex gap-2 flex-wrap">
                            <a
                              href={`${BACKEND_URL}/api/widget?client=${client.clientId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid="view-widget-btn"
                              className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium hover:bg-green-200"
                            >
                              View Widget
                            </a>
                            <button
                              data-testid="get-embed-btn"
                              onClick={() => setShowEmbedModal(client.clientId)}
                              className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium hover:bg-blue-200"
                            >
                              Get Embed Code
                            </button>
                            <button
                              data-testid="toggle-active-btn"
                              onClick={() => handleToggleActive(client.clientId, client.isActive)}
                              className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium hover:bg-gray-200"
                            >
                              {client.isActive ? "Deactivate" : "Activate"}
                            </button>
                            <button
                              data-testid="delete-client-btn"
                              onClick={() => handleDeleteClient(client.clientId)}
                              className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium hover:bg-red-200"
                            >
                              Delete
                            </button>
                            <button
                              data-testid="edit-client-btn"
                              onClick={() => setEditingClient({...client})}
                              className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs font-medium hover:bg-yellow-200"
                            >
                              Edit
                            </button>
                            <button
                              data-testid="refresh-reviews-btn"
                              onClick={() => handleRefreshReviews(client.clientId)}
                              disabled={refreshingClient === client.clientId}
                              className={`px-2 py-1 rounded text-xs font-medium ${
                                refreshingClient === client.clientId
                                  ? "bg-gray-100 text-gray-400 cursor-wait"
                                  : "bg-purple-100 text-purple-700 hover:bg-purple-200"
                              }`}
                            >
                              {refreshingClient === client.clientId ? "Refreshing..." : "Refresh Reviews"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* Edit Client Modal */}
        {editingClient && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-semibold text-gray-900">
                    Edit Client: {editingClient.clientId}
                  </h3>
                  <button
                    onClick={() => setEditingClient(null)}
                    className="text-gray-500 hover:text-gray-700 text-2xl"
                  >
                    ×
                  </button>
                </div>

                <form onSubmit={(e) => { e.preventDefault(); handleUpdateClient(editingClient.clientId); }}>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Client ID
                      </label>
                      <input
                        type="text"
                        value={editingClient.clientId}
                        disabled
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">Client ID cannot be changed</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Google Business URL *
                      </label>
                      <input
                        type="url"
                        data-testid="edit-business-url"
                        value={editingClient.businessUrl}
                        onChange={(e) => setEditingClient({...editingClient, businessUrl: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Google Review Link (for "Leave a Review" button)
                      </label>
                      <input
                        type="url"
                        data-testid="edit-review-link"
                        value={editingClient.googleReviewLink || ""}
                        onChange={(e) => setEditingClient({...editingClient, googleReviewLink: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="https://g.page/r/..."
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Theme Color
                        </label>
                        <input
                          type="color"
                          data-testid="edit-theme-color"
                          value={editingClient.themeAccentColor}
                          onChange={(e) => setEditingClient({...editingClient, themeAccentColor: e.target.value})}
                          className="w-full h-10 border border-gray-300 rounded-lg cursor-pointer"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Max Reviews
                        </label>
                        <input
                          type="number"
                          data-testid="edit-max-reviews"
                          value={editingClient.maxReviews}
                          onChange={(e) => setEditingClient({...editingClient, maxReviews: parseInt(e.target.value)})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          min="1"
                          max="20"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          data-testid="edit-is-active"
                          checked={editingClient.isActive}
                          onChange={(e) => setEditingClient({...editingClient, isActive: e.target.checked})}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-gray-700">Active</span>
                      </label>
                    </div>
                  </div>

                  <div className="flex gap-3 mt-6">
                    <button
                      type="submit"
                      data-testid="save-edit-btn"
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
                    >
                      Save Changes
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingClient(null)}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Embed Code Modal */}
        {showEmbedModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-semibold text-gray-900">
                    Embed Code for: {showEmbedModal}
                  </h3>
                  <button
                    onClick={() => setShowEmbedModal(null)}
                    className="text-gray-500 hover:text-gray-700 text-2xl"
                  >
                    ×
                  </button>
                </div>

                <div className="mb-6">
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Iframe Embed (Recommended)
                    </label>
                    <button
                      onClick={() => copyToClipboard(getIframeCode(showEmbedModal), "iframe")}
                      className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                        copiedCode === "iframe"
                          ? "bg-green-100 text-green-700"
                          : "bg-blue-100 text-blue-700 hover:bg-blue-200"
                      }`}
                    >
                      {copiedCode === "iframe" ? "✓ Copied!" : "Copy Code"}
                    </button>
                  </div>
                  <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm overflow-x-auto">
                    <pre>{getIframeCode(showEmbedModal)}</pre>
                  </div>
                </div>

                <div className="mb-6">
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Script Embed (Alternative)
                    </label>
                    <button
                      onClick={() => copyToClipboard(getScriptCode(showEmbedModal), "script")}
                      className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                        copiedCode === "script"
                          ? "bg-green-100 text-green-700"
                          : "bg-blue-100 text-blue-700 hover:bg-blue-200"
                      }`}
                    >
                      {copiedCode === "script" ? "✓ Copied!" : "Copy Code"}
                    </button>
                  </div>
                  <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm overflow-x-auto">
                    <pre>{getScriptCode(showEmbedModal)}</pre>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Preview
                  </label>
                  <iframe
                    src={`${BACKEND_URL}/api/widget?client=${showEmbedModal}`}
                    width="100%"
                    height="300"
                    style={{ border: "1px solid #e5e7eb", borderRadius: "8px" }}
                    title="Widget Preview"
                  />
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={() => setShowEmbedModal(null)}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {selectedTab === "feedback" && (
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Customer Feedback</h2>
              {feedback.length === 0 ? (
                <p className="text-gray-600" data-testid="no-feedback">No feedback submitted yet.</p>
              ) : (
                <div className="space-y-4">
                  {feedback.map((fb) => (
                    <div key={fb.id} className="border border-gray-200 rounded-lg p-4" data-testid="feedback-item">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <span className="font-medium text-gray-900" data-testid="feedback-client-id">{fb.clientId}</span>
                          {fb.name && <span className="text-gray-600 ml-2">- {fb.name}</span>}
                        </div>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          fb.rating === "bad"
                            ? "bg-red-100 text-red-800"
                            : "bg-yellow-100 text-yellow-800"
                        }`} data-testid="feedback-rating">
                          {fb.rating}
                        </span>
                      </div>
                      {fb.email && (
                        <p className="text-sm text-gray-600 mb-2">
                          Email: {fb.email}
                        </p>
                      )}
                      <p className="text-gray-700 mb-2" data-testid="feedback-message">{fb.message}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(fb.createdAt).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Demo = () => {
  const navigate = useNavigate();
  const [clientId, setClientId] = useState("demo-spokane-fireplace");

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-gray-900">Widget Demo</h1>
            <button
              onClick={() => navigate("/")}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              ← Back to Home
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold mb-4">Test Widget</h2>
          <p className="text-gray-600 mb-4">
            Enter a client ID to preview the widget. Create a client in the admin dashboard first.
          </p>
          <div className="flex gap-4">
            <input
              type="text"
              data-testid="demo-client-input"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Enter client ID"
            />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4">Widget Preview</h3>
          <div data-testid="widget-preview">
            <iframe
              src={`${BACKEND_URL}/api/widget?client=${clientId}`}
              width="100%"
              height="440"
              style={{ border: "none", borderRadius: "8px" }}
              title="Widget Preview"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/demo" element={<Demo />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;